import { forwardRef, InputHTMLAttributes, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Search, X, Loader2 } from 'lucide-react';

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  isLoading?: boolean;
  debounceMs?: number;
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, value: controlledValue, onChange, onSearch, isLoading, debounceMs = 300, placeholder = 'Search...', ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(controlledValue || '');
    const value = controlledValue !== undefined ? controlledValue : internalValue;

    useEffect(() => {
      if (controlledValue !== undefined) {
        setInternalValue(controlledValue);
      }
    }, [controlledValue]);

    useEffect(() => {
      if (!onSearch) return;

      const timer = setTimeout(() => {
        onSearch(value);
      }, debounceMs);

      return () => clearTimeout(timer);
    }, [value, debounceMs, onSearch]);

    const handleChange = (newValue: string) => {
      setInternalValue(newValue);
      onChange?.(newValue);
    };

    const handleClear = () => {
      handleChange('');
      onSearch?.('');
    };

    return (
      <div className={clsx('relative', className)}>
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
          ) : (
            <Search className="h-4 w-4 text-gray-400" />
          )}
        </div>
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          className={clsx(
            'block w-full rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500',
            'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20',
            'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-200',
            'pl-10 pr-10 py-2 text-sm'
          )}
          placeholder={placeholder}
          {...props}
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export { SearchInput };
